import React, { useState } from 'react';
import { FaTree, FaGripVertical } from "react-icons/fa";
import type { TreeCardData } from '../../types';
import styles from './TreeCard.module.css';

interface TreeCardProps {
  tree: TreeCardData;
  onDrag: (tree: TreeCardData, position: { x: number; y: number }) => void;
  onSelect: (tree: TreeCardData) => void;
  isSelected?: boolean;
}

const TreeCard: React.FC<TreeCardProps> = ({
  tree,
  onDrag,
  onSelect,
  isSelected = false
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    const treeDataString = JSON.stringify(tree);
    e.dataTransfer.setData('text/plain', treeDataString);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(false);
    const rect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    onDrag?.(tree, position);
  };

  const handleClick = () => {
    onSelect?.(tree);
  };

  return (
    <div
      className={`${styles.treeCard} ${isDragging ? styles.dragging : ''} ${isSelected ? styles.selected : ''}`}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`${tree.name} tree species card`}
    >
      {tree.dragIndicator && (
        <div className={styles.dragIndicator} aria-hidden="true">
          <FaGripVertical />
        </div>
      )}
      
      <div className={styles.treeImage} aria-hidden="true">
        {tree.image && tree.image.startsWith('/') ? (
          <img 
            src={tree.image} 
            alt={tree.name}
            className={styles.treeImageImg}
            onError={(e) => {
              // Fallback to emoji if image fails to load
              const img = e.currentTarget as HTMLImageElement;
              const sibling = img.nextElementSibling as HTMLElement;
              img.style.display = 'none';
              if (sibling) sibling.style.display = 'block';
            }}
          />
        ) : null}
        <span 
          className={styles.treeImageEmoji}
          style={{ display: tree.image && tree.image.startsWith('/') ? 'none' : 'block' }}
        >
          {tree.image && tree.image.startsWith('/') ? <FaTree /> : tree.image}
        </span>
      </div>
      
      <div className={styles.treeName}>
        {tree.name}
      </div>
      
      <div className={styles.treeStats}>
        <div className={styles.statLine}>Height: {tree.stats.height}</div>
        <div className={styles.statLine}>CO₂: {tree.stats.co2Absorption}</div>
        <div className={styles.statLine}>
          {tree.stats.tolerance.includes('tolerance') 
            ? tree.stats.tolerance 
            : `${tree.stats.tolerance} tolerance`
          }
        </div>
        {tree.stats.growthRate && (
          <div className={styles.statLine}>Growth: {tree.stats.growthRate}</div>
        )}
        {tree.stats.lifespan && (
          <div className={styles.statLine}>Life: {tree.stats.lifespan}</div>
        )}
      </div>
    </div>
  );
};

export default TreeCard;